<template>
    <div width="100%">
        <template v-if="mode !== 'Payment' && isAccountingUser">
            <v-data-table
                :headers="headers"
                :items="filteredAccountingLines"
                fixed-header
                max-height="60vh"
                border
                :items-per-page="-1"
                v-if="!$vuetify.display.mobile"
                density="compact"
                class="table"
            >
                <template v-slot:item="props">
                    <tr :class="props.item.selected ? 'info' : ''">
                        <td class="text-left">{{ formatDate(props.item.date) }}</td>
                        <td class="text-left">{{ getItemAccountableAccountTitle(props.item) }}</td>
                        <td class="text-left">
                            <v-chip-tag color="tagFree" v-if="props.item.tag" class="text mr-1">{{
                                props.item.tag.name
                            }}</v-chip-tag>
                            <template v-if="props.item.tags">
                                <v-chip-tag
                                    :ripple="false"
                                    v-for="(tag, index) of props.item.tags"
                                    :key="index"
                                    class="text mr-1"
                                >
                                    <span :style="'color:' + labelTextColor + ';'">{{
                                        tag.name
                                    }}</span>
                                </v-chip-tag>
                            </template>
                            {{ props.item.wording }}
                        </td>
                        <td class="text-left" v-if="
                            !isShortScreen"
                        >
                            {{ getThirdPartyName(props.item) }}
                        </td>
                        <td class="text-left" v-if="showDueDateColumn">
                            {{ formatDate(props.item.dueDate) }}
                        </td>
                        <td class="text-right" v-if="props.item.debit">
                            {{ newCommonFormatCurrency(props.item.debit, undefined) }}
                        </td>
                        <td class="text-right" v-else>-</td>
                        <td class="text-right" v-if="props.item.credit">
                            {{ newCommonFormatCurrency(props.item.credit, undefined) }}
                        </td>
                        <td class="text-right" v-else>-</td>

                        <td class="text-center" v-if="props.item.reconciliationCallToAction">
                            <v-btn
                                elevation="0"
                                @click="openReconciliation(props.item)"
                                size="x-small"
                            >
                                {{ props.item.reconciliationCallToAction }}
                            </v-btn>
                        </td>
                        <td class="text-center" v-else>
                            <v-icon color="primary" size="large">$dot</v-icon>
                        </td>
                    </tr>
                </template>
                <template v-slot:body.append>
                    <tr>
                        <td :colspan="containsThirdParty ? 4 : 2" />
                        <td class="text-right"><strong>Total</strong></td>
                        <td class="text-right">
                            <strong>{{ newCommonFormatCurrency(totalDebit, undefined) }}</strong>
                        </td>
                        <td class="text-right">
                            <strong>{{ newCommonFormatCurrency(totalCredit, undefined) }}</strong>
                        </td>
                    </tr>
                </template>
            </v-data-table>
            <v-row no-gutters v-else>
                <v-list style="background: transparent">
                    <v-list-item
                        v-for="(item, index) in accountingLines"
                        :key="index"
                        class="my-1 mx-0"
                        :ripple="false"
                    >
                        <v-list-item-title class="wrapper">
                            <p :class="extendedMode ? '' : 'text-wrapped'">
                                {{ item.wording }}
                            </p>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                            {{ getItemAccountableAccountTitle(item) }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                            {{ getThirdPartyName(item) }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle class="mt-2 d-flex align-center" v-if="extendedMode">
                            <v-icon size="small" location="start">$oss-calendar</v-icon
                            >{{ formatDate(item.dueDate) }}
                        </v-list-item-subtitle>

                        <template v-slot:append>
                            <span
                                :class="
                                    getAmount(item) > 0
                                        ? 'font-weight-bold primary--text'
                                        : 'text-red font-weight-bold'
                                "
                            >
                                {{ newCommonFormatCurrency(getAmount(item), undefined) }}
                            </span>
                        </template>
                    </v-list-item>
                </v-list>
            </v-row>
        </template>
        <template v-if="mode === 'Payment'">
            <!-- <v-list style="background: transparent;">
                <v-list-item
                    v-for="(item, index) in accountingLines"
                    :key="index"
                    class="my-1 mx-0 oss-list-item"
                    :ripple="false"
                >

                    <v-list-item-subtitle>
                        {{ getItemAccountableAccountTitle(item) }}
                    </v-list-item-subtitle>

                    <template v-slot:append>
                        <span :class="getAmount(item) > 0 ? 'font-weight-bold primary--text' : 'text-red font-weight-bold'">
                            {{ newCommonFormatCurrency(getAmount(item)) }}
                        </span>
                    </template>
                </v-list-item>
            </v-list> -->
            <oss-data-table
                :headers="detailHeaders"
                :items="filteredAccountingLines"
                class="table"
                :striped="true"
                :dense="true"
                fixed-header
                checkbox-color="primary"
            >
                <template v-slot:item="props">
                    <tr>
                        <td
                            v-if="candidateForPaymentAccountingLineCount"
                            align="center"
                            style="max-width: 30px"
                        >
                            <v-btn-icon
                                :icon="
                                    props.item.selected
                                        ? '$oss-circle-check'
                                        : '$oss-circle-uncheck'
                                "
                                size="x-small"
                                @click.stop="props.item.selected = !props.item.selected"
                                v-if="props.item.paymentStatus === 'TODO'"
                                data-cy="AccountingItemStatic-selectioner"
                            />
                        </td>
                        <td>
                            <span>{{ getItemAccountableAccountTitle(props.item) }}</span>
                        </td>
                        <td v-if="showDueDateColumn" align="center" style="cursor: pointer">
                            <span
                                v-if="!!props.item.paymentStatus"
                                @click.stop="opendueDateToEditDialog(props.item)"
                            >
                                {{ getDueDate(props.item) }}
                                <v-icon size="small"> $oss-edit </v-icon>
                            </span>
                        </td>
                        <td align="right">
                            <span
                                :class="
                                    getAmount(props.item) > 0
                                        ? 'font-weight-bold primary--text'
                                        : 'text-red font-weight-bold'
                                "
                                >{{
                                    newCommonFormatCurrency(getAmount(props.item), undefined)
                                }}</span
                            >
                        </td>
                        <td
                            v-if="candidateForPaymentAccountingLineCount"
                            align="center"
                            style="max-width: 60px"
                        >
                            <v-chip
                                label
                                class="px-1"
                                :color="props.item.paymentStatus === 'DONE' ? 'secondary' : 'red'"
                                v-if="props.item.paymentStatus"
                                size="x-small"
                                variant="outlined"
                            >
                                {{ getPaymentStatusTitle(props.item) }}
                            </v-chip>
                        </td>
                    </tr>
                    <tr v-if="showTags">
                        <td />
                        <td
                            :colspan="
                                showDueDateColumn
                                    ? candidateForPaymentAccountingLineCount
                                        ? 4
                                        : 3
                                    : candidateForPaymentAccountingLineCount
                                      ? 3
                                      : 2
                            "
                        >
                            <FreeTag
                                :companyTags="allCompanyTags"
                                :company="selectedCompany"
                                :documentType="type"
                                :accountingLine="props.item"
                            />
                        </td>
                    </tr>
                </template>
            </oss-data-table>

            <!-- <v-row no-gutters v-for="(accountingLine, index) in accountingLines" :key="index">
                <v-col :cols="3" class="d-flex align-center pl-2" v-if="candidateForPaymentAccountingLineCount">
                      <v-btn
                        icon
                        color="primary"
                        @click.stop="accountingLine.selected = !accountingLine.selected"
                        v-if="accountingLine.paymentStatus==='TODO'"
                    >
                        <v-icon  size="small" color="primary">
                            {{ accountingLine.selected ? '$oss-circle-check' : '$oss-circle-uncheck' }}
                        </v-icon>
                    </v-btn>
                    <v-chip
                         size=“x-small”
                        label
                        outlined
                        class="px-1"
                        :color="accountingLine.paymentStatus === 'DONE' ? 'secondary' : 'red'"
                        v-if="accountingLine.paymentStatus"
                    > {{getPaymentStatusTitle(accountingLine)}}
                    </v-chip>
                </v-col>
                <v-col :cols="candidateForPaymentAccountingLineCount ? 6:9" class="d-flex justify-start align-center pl-2">
                    <span>{{getItemAccountableAccountTitle(accountingLine)}}</span>
                </v-col>
                <v-col cols=3 class="d-flex justify-end align-center px-2">
                    <span :class="getAmount(accountingLine) > 0 ? 'font-weight-bold primary--text' : 'text-red font-weight-bold'">{{newCommonFormatCurrency(getAmount(accountingLine), undefined) }}</span>
                </v-col>
            </v-row> -->
            <div class="pt-2 pl-2 d-flex space-x-1">
                <div class="d-flex align-center justify-center">
                    <v-btn
                        rounded
                        :disabled="!selectedAccountingLinesForPayment.length"
                        @click.stop="payDialog = true"
                        v-if="toPayAccountingLineCount"
                        elevation="0"
                        data-cy="AccountingItemStatic-paiement"
                        size="x-small"
                    >
                        Gérer le paiement
                    </v-btn>
                </div>
                <div>
                    <v-switch
                        v-model="showTags"
                        density="compact"
                        elevation="0"
                        hide-details
                        class="pl-2"
                        label="Afficher les étiquettes"
                    />
                </div>
            </div>
        </template>

        <oss-dialog
            v-model="deleteDocumentDialog"
            :title="'Supprimer les opérations diverses'"
            @valid="deleteDocument"
            width="800px"
        >
            <div class="body-1">
                &#xca;tes-vous sur de vouloir supprimer les opérations diverses ?
            </div>
        </oss-dialog>
        <oss-dialog
            v-if="dueDateDialog"
            v-model="dueDateDialog"
            @close="dueDateDialog = false"
            :title="'Edition échéance'"
            :width="$vuetify.display.mobile ? '100wv' : '50wv'"
        >
            <template v-slot:actions>
                <v-btn-primary
                    rounded
                    block
                    @click="updateAccountingLineDueDate()"
                    :disabled="!dueDateValid"
                    size="x-large"
                    >Modifier</v-btn-primary
                >
            </template>

            <v-form v-model="dueDateValid" class="w-100 pb-3">
                <div class="w-100">
                    <ossDatePicker-filled
                        v-model="dueDateToEdit"
                        hide-details
                        :textFieldReadOnly="false"
                        :notRequired="true"
                    >
                    </ossDatePicker-filled>
                </div>
            </v-form>
        </oss-dialog>
        <oss-dialog v-model="editDialog" :actions="actionsEditionDialog">
            <div style="width: 90vw">
                <AccountingItemEdition2
                    v-if="editDialog"
                    :companyId="companyId"
                    :documentId="documentId"
                    :actions="actionsEditionDialog"
                    @upsert="onUpsert"
                />
            </div>
        </oss-dialog>
        <!-- <AccountingItemEdition
            v-model="editDialog"
            :companyId="companyId"
            :documentId="documentId"
            @upsert="onUpsert"
            v-if="editDialog"
        ></AccountingItemEdition> -->
        <PaymentManagement
            v-model="payDialog"
            :companyId="companyId"
            :inInvoiceNumber="invoiceNumber"
            :accountingLines="selectedAccountingLinesForPayment"
            :forCustomer="isForCustomer"
            @actionDone="refresh()"
            v-if="payDialog"
        />
    </div>
</template>

<script lang="ts">
import colors from "vuetify/lib/util/colors";
import OssDataTable from "./ossComponents/ossDataTable2.vue";

import { AccountingItemBase } from "./AccountingItemBase";
import AccountingItemEdition2 from "./accounting/accountingItem/Edition.vue";
import PaymentManagement from "./pay/PaymentManagement.vue";

import UPDATE_ACCOUNTINGLINE_DUEDATE from "../graphql/accountingLine/UpdateAccountingLineDueDate.gql";
import moment from "moment-timezone";
import FreeTag from "./tag/FreeTag.vue";
import { getDBFormattedDate } from "../services/dateServices";
import { newCommonFormatCurrency } from "../../../backend/src/common/services/mathServices";
import { DialogAction } from "@/services/dialogAction";
import { Component, toNative } from "vue-facing-decorator";

import { DataTableHeaders } from "@/types/vuetify";

@Component({
    components: {
        AccountingItemEdition2,
        PaymentManagement,
        OssDataTable,
        FreeTag,
    },
    emits: ["upsert", "update"],
})
export class AccountingItemStatic extends AccountingItemBase {
    payDialog: boolean = false;
    showTags: boolean = false;

    actionsEditionDialog = {
        action: new DialogAction(),
    };

    newCommonFormatCurrency(value, options = undefined) {
        return newCommonFormatCurrency(value, undefined);
    }

    get activateTagguing() {
        return this.showTags;
    }

    get headers(): DataTableHeaders {
        const headers = [
            { title: "Date", value: "date", align: "center" },
            { title: "Compte Comptable", value: "accountableAccount", align: "start" },
            { title: "Nom", value: "wording", align: "start" },
        ];
        if (this.containsThirdParty && !this.isShortScreen) {
            headers.push({
                title: "Client | Fournisseur | Employé | Banque",
                value: "thirdPartyCompany",
                align: "start",
            });
        }
        if (this.showDueDateColumn) {
            headers.push({ title: "Echéance", value: "dueDate", align: "center" });
        }

        headers.push({ title: "Débit", value: "debit", align: "end" });
        headers.push({ title: "Crédit", value: "credit", align: "end" });

        if (this.isAccountingUser) {
            headers.push({ title: "Lettrage", value: "reconciliation", align: "center" });
        }
        return headers as DataTableHeaders;
    }

    get isStatic() {
        return true;
    }

    editDialog = false;

    dueDateDialog = false;

    dueDateValid = false;

    extendedMode = false;

    dueDateToEdit = null;

    selectedAccountingLine: any = null;

    onUpsert() {
        this.refresh();
        this.$emit("upsert");
    }

    openEditDialog() {
        this.editDialog = true;
    }

    // get disabledEdit() {
    //     if (this.accountingItemImport && this.accountingItemImportId) {
    //         if (this.accountingItemImport.accountingPeriod && (this.accountingItemImport.accountingPeriod.closed || this.accountingItemImport.accountingPeriod.temporaryClosed)) {
    //             return true
    //         }
    //         return false
    //     } else
    //     // if (!!this.accountingItemImportId) return true
    //     if (this.type !== "MISCELLANEOUS_OPERATION") return true
    //     return false
    // }

    get showEditOrDelete() {
        if (this.$vuetify.display.mobile) return false;
        if (!!this.accountingItemImportId) return true;
        if (this.type === "MISCELLANEOUS_OPERATION") return true;
        return false;
    }

    getAmount(item) {
        return item.debit - item.credit;
    }

    get totalDebit() {
        let amount = 0;
        for (const al of this.accountingLines) {
            if (al.tag) {
                continue;
            }
            if (al.amountSigned > 0) amount += al.amountSigned;
        }
        return amount;
    }

    get totalCredit() {
        let amount = 0;
        for (const al of this.accountingLines) {
            if (al.tag) {
                continue;
            }
            if (al.amountSigned < 0) amount -= al.amountSigned;
        }
        return amount;
    }

    get isForCustomer() {
        if (this.type === "SALES_INVOICE") {
            return true;
        }
        return false;
    }

    get filteredAccountingLines() {
        const filteredAccountingLines = [];
        for (const al of this.accountingLines) {
            if (al.type === "ORIGINAL") {
                continue;
            }
            filteredAccountingLines.push(al);
        }
        return filteredAccountingLines;
    }

    get containsThirdParty() {
        for (const al of this.accountingLines) {
            if (al.thirdPartyCompany) {
                return true;
            }
            if (al.user) {
                return true;
            }
            if (al.bankAccount) {
                return true;
            }
        }
        return false;
    }

    get labelTextColor() {
        return colors.grey.darken2;
    }

    async updateAccountingLineDueDate() {
        if (this.selectedAccountingLine?.id && this.dueDateToEdit !== undefined) {
            await this.$apollo.mutate({
                mutation: UPDATE_ACCOUNTINGLINE_DUEDATE,
                variables: {
                    accountingLineId: this.selectedAccountingLine.id,
                    dueDate: this.dueDateToEdit ? getDBFormattedDate(this.dueDateToEdit) : null,
                },
            });
            this.$emit("update");

            this.selectedAccountingLine = null;
        }
        this.dueDateDialog = false;
    }

    //
    // dueDate
    //

    getDueDate(item) {
        if (item?.paymentStatus && item?.documentId) {
            if (!item?.dueDate) {
                return "";
            }
        }

        if (item.dueDate) {
            return moment(item.dueDate).format("DD/MM/YYYY");
        }
        return "";
    }

    opendueDateToEditDialog(item) {
        this.selectedAccountingLine = item;
        this.dueDateToEdit = item.dueDate;
        this.dueDateDialog = true;
    }
}

export default toNative(AccountingItemStatic);
</script>

<style scoped>
.wrapper {
    /* max-width: 200px; */
    white-space: normal;
}

.text-wrapped {
    overflow: hidden;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}
</style>
